<template>
  <div>
    <div class="flex justify-between">
      <GrmBtn color="gray" @click="SET_VIEW('testbank__exam_list')">{{ $t("testBank.goBack") }}</GrmBtn>
      <GrmBtn @click="onCreateExam">{{ $t("common.register") }}</GrmBtn>
    </div>
    <div class="mt-4 -mx-3 py-8 px-10 bg-gray-100 lg:mt-10 lg:-mx-10 lg:px10">
      <div class="flex flex-col gap-y-6">
        <div>
          <p class="w-max border border-blue-400 rounded-xl px-3 bg-white text-sm text-blue-400 leading-6">{{ $t("testBank.style") }}</p>
          <div class="mt-3 flex gap-x-2">
            <GrmBtn :color="testbankObj.examType === 'QUIZ' ? 'purple' : 'gray'" hoverColor="none" @click="onSelectExamType('QUIZ')">{{ $t("testBank.write") }}</GrmBtn>
            <GrmBtn :color="testbankObj.examType === 'SURVEY' ? 'green' : 'gray'" hoverColor="none" @click="onSelectExamType('SURVEY')">{{ $t("testBank.survey") }}</GrmBtn>
          </div>
        </div>
        <div>
          <div class="w-max border border-blue-400 rounded-xl px-3 bg-white text-sm text-blue-400 leading-6">{{ $t("setting.name.title") }}</div>
          <p class="mt-3">
            <GrmTextField bgColor="white" class="w-full" v-model="testbankObj.examTitle" :placeholder="isMobile ? $t('testBank.alert.shortName') : $t('testBank.alert.name')" ref="examTitle" />
          </p>
        </div>
        <div v-if="testbankObj.examType === 'QUIZ'" class="border-t border-gray-300 pt-6 flex flex-col items-center">
          <p class="font-bold">{{ $t("testBank.answerTable") }}</p>
          <TestbankResultCorrectTable class="mt-4" :questionList="testbankObj.questionList" />
        </div>
      </div>
    </div>

    <TestbankQuestionList type="edit"
                          :testbankObj="testbankObj"
                          :questionList="testbankObj.questionList"
                          @updateQuestionList="onUpdateQuestionList"
    />
  </div>
</template>

<script>
import {mapActions, mapMutations, mapState} from "vuex";

import GrmBtn from "@/components/Common/button/GrmBtn.vue";
import GrmTextField from "@/components/Common/textField/GrmTextField.vue";
import TestbankQuestionList from "@/components/Room/Testbank/Question/TestbankQuestionList.vue";
import TestbankResultCorrectTable from "@/components/Room/Testbank/Result/TestbankResultCorrectTable.vue";
import {Device} from "@/utils";

export default {
  name: 'RoomTestbankExamCreate',
  components: {
    GrmBtn,
    GrmTextField,
    TestbankQuestionList,
    TestbankResultCorrectTable,
  },
  data() {
    return {
      testbankObj: {
        bankId: '',
        examTitle: '',
        examType: 'QUIZ',
        regType: 'direct',
        questionList: [
          {
            questionTitle: '',
            questionType: 'CHOICE',
            correctAnswerCnt: 0,
            questionScore: 1,
            questionOrder: 0,
            answerList: [
              { answerTitle: '', correct: false, answerOrder: 0 },
              { answerTitle: '', correct: false, answerOrder: 1 },
            ]
          },
          {
            questionTitle: '',
            questionType: 'SUBJECTIVE',
            correctAnswerCnt: 1,
            questionScore: 1,
            questionOrder: 1,
            answerList: [
              { answerText: '', correct: true, answerOrder: 0 },
            ]
          },
        ],
      },
    }
  },
  computed: {
    ...mapState('testbank', [
      'testbank',
      'testbankExamList',
    ]),

    examTypeColor() {
      return this.testbankObj.examType === 'QUIZ' ? 'purple' : 'green'
    },
    isMobile() {
      return Device.isMobile();
    }
  },
  methods: {
    ...mapMutations('testbank', [
      'SET_VIEW',
    ]),
    ...mapActions('testbank', [
      'createTestbankExam',
    ]),

    onCreateExam() {
      if (this.testbankObj.examTitle === '') {
        this.$alert({
          text: this.$t('testBank.alert.name'),
          onlyOk: true,
          ok: () => {
            this.$refs.examTitle.$refs.input.focus();
          }
        });
        return;
      }

      for (let i = 0; i < this.testbankObj.questionList.length; i++) {
        const question = this.testbankObj.questionList[i];
        const questionNum = i + 1;

        if (question.questionTitle === '') {
          this.$alert({
            text: this.$t('testBank.alert.question.name', { '0' : questionNum}),
            onlyOk: true,
            ok: () => {
              this.onMoveQuestion(questionNum);
            }
          });
          return;
        }

        if (this.testbankObj.examType === 'QUIZ') {
          for (let j = 0; j < question.answerList.length; j++) {
            const answer = question.answerList[j];
            const answerNum = j + 1;

            if (answer.answerTitle === '' || answer.answerText === '') {
              this.$alert({
                text: question.questionType === 'CHOICE'
                    ? this.$t('testBank.alert.question.select', { '0' : questionNum, '1' : answerNum })
                    : this.$t('testBank.alert.question.answer', { '0' : questionNum, '1' : answerNum }),
                onlyOk: true,
                ok: () => {
                  this.onMoveQuestion(questionNum);
                }
              });
              return;
            }
          }
        }

        const isNotSelectedAnswer =
          this.testbankObj.examType === 'QUIZ'
          && question.questionType === 'CHOICE'
          && question.answerList.filter(answer => answer.correct).length === 0;

        if (isNotSelectedAnswer) {
          this.$alert({
            text: this.$t('testBank.alert.question.choice', { '0' : questionNum,}),
            onlyOk: true,
            ok: () => {
              this.onMoveQuestion(questionNum);
            }
          });
          return;
        }
        if (this.testbankObj.examType === 'SURVEY') {
          delete this.testbankObj.questionList[i].questionScore;
          delete this.testbankObj.questionList[i].correctAnswerCnt;
        }
      }

      this.testbankObj = {
        ...this.testbankObj,
        bankId: this.testbank.bankId,
        questionList: this.testbankObj.questionList.map((question, index) => {
          return {
            ...question,
            questionOrder: index,
            answerList: question.answerList.map((answer, index) => {
              return {
                ...answer,
                answerOrder: index,
              }
            })
          }
        })
      }

      this.createTestbankExam(this.testbankObj);
    },
    onSelectExamType(type) {
      this.testbankObj.examType = type
    },
    onUpdateQuestionList(questionList) {
      this.testbankObj.questionList = questionList;
    },
    onMoveQuestion(questionNum) {
      document.querySelector(`#question_${questionNum}`).scrollIntoView();
    }
  }
}
</script>

<style lang="scss" scoped>
.room-exam-create {
  padding-bottom: 40px;
}
.room-exam-create-body {
  margin-top: 40px;
}
.room-exam-create-footer {
  margin-top: 40px;
  display: flex;
  justify-content: center;
}
.room-exam-create-button-container {
  display: flex;
  gap: 0 10px;
}
.room-exam-create-button {
  transition: 0.1s ease-out;
  &.add {
    padding: 0 60px;
    font-size: 14px;
    color: #fff;
    line-height: 40px;
    &.obj {
      background: #737898 url('~@/assets/images/exam_obj_white.svg') no-repeat center left 10px / 20px auto;
      &:hover {
        background: #1bc1c1 url('~@/assets/images/exam_obj_white.svg') no-repeat center left 10px / 20px auto;
      }
    }
    &.sub {
      background: #737898 url('~@/assets/images/exam_sub_white.svg') no-repeat center left 10px / 20px auto;
      &:hover {
        background: #f90 url('~@/assets/images/exam_sub_white.svg') no-repeat center left 10px / 20px auto;
      }
    }
    &:hover {
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
    }
  }
}
</style>